import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { fetchActiveTickets, fetchTicketHistory, fetchMyGiftCards } from '../../api/starviewApi';

// The signed-in home: balances, upcoming tickets, history, gift cards -
// the same account that powers the app, so everything here syncs with it.
const card = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14, padding: '18px 20px' };
const label = { fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', opacity: 0.65 };

function Balance({ value, name, accent }) {
  return (
    <div style={{ ...card, minWidth: 150, flex: 1 }}>
      <div style={{ fontSize: 26, fontWeight: 800, color: accent, fontVariantNumeric: 'tabular-nums' }}>{Number(value || 0).toLocaleString()}</div>
      <div style={label}>{name}</div>
    </div>
  );
}

function TicketRow({ t }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.08)', flexWrap: 'wrap' }}>
      <div>
        <strong>{t.movie_title || t.movieTitle}</strong>
        <div style={{ opacity: 0.7, fontSize: 13 }}>{t.country} · Seat {t.seat_id || t.seatId} · {t.showtime}</div>
      </div>
      <div style={{ textAlign: 'right', fontSize: 13 }}>
        <span style={{
          padding: '3px 10px', borderRadius: 999, fontWeight: 700, fontSize: 11, letterSpacing: '0.06em',
          background: (t.status === 'active') ? 'rgba(46,125,79,0.25)' : 'rgba(255,255,255,0.1)',
          color: (t.status === 'active') ? '#7fd6a4' : 'rgba(255,255,255,0.7)',
        }}>{(t.status || '').toUpperCase()}</span>
      </div>
    </div>
  );
}

export default function AccountHub() {
  const { user, isAuthenticated, authFetch, logout } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState(null);
  const [history, setHistory] = useState(null);
  const [giftCards, setGiftCards] = useState(null);
  const [tab, setTab] = useState('upcoming');

  useEffect(() => {
    if (!isAuthenticated) { navigate('/Login?next=/account'); return; }
    fetchActiveTickets(authFetch).then(setActive).catch(() => setActive([]));
    fetchTicketHistory(authFetch).then(setHistory).catch(() => setHistory([]));
    fetchMyGiftCards(authFetch).then(setGiftCards).catch(() => setGiftCards([]));
  }, [isAuthenticated, authFetch, navigate]);

  if (!isAuthenticated) return null;
  const tickets = tab === 'upcoming' ? active : history;

  return (
    <main style={{ minHeight: '70vh', background: '#0b0c1e', color: '#fff', padding: '110px 20px 60px' }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <p style={{ ...label, color: '#CFAA41' }}>My StarView</p>
        <h1 style={{ margin: '4px 0 4px', fontSize: 30 }}>
          {user?.username ? `Welcome back, ${user.username}` : 'My account'}
        </h1>
        <p style={{ opacity: 0.7, marginTop: 0 }}>One account across the app and this website - tickets, Galaxies and points sync instantly.</p>

        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', margin: '22px 0' }}>
          <Balance value={user?.galaxies} name="Galaxies" accent="#CFAA41" />
          <Balance value={user?.points} name="Points" accent="#38BDF8" />
          <Balance value={user?.tickets} name="Ticket credits" accent="#7fd6a4" />
        </div>

        <div style={{ ...card, marginBottom: 18 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            {['upcoming', 'history'].map((k) => (
              <button key={k} onClick={() => setTab(k)}
                style={{ padding: '7px 16px', borderRadius: 999, cursor: 'pointer', border: '1px solid rgba(255,255,255,0.25)',
                         background: tab === k ? '#CFAA41' : 'transparent', color: tab === k ? '#141021' : '#fff', fontWeight: 700 }}>
                {k === 'upcoming' ? 'My tickets' : 'History'}
              </button>
            ))}
          </div>
          {tickets == null ? <p style={{ opacity: 0.6 }}>Loading…</p>
            : tickets.length === 0 ? (
              <p style={{ opacity: 0.7 }}>
                {tab === 'upcoming' ? 'No upcoming tickets - ' : 'No past tickets yet - '}
                <Link to="/Showtimes" style={{ color: '#38BDF8' }}>browse showtimes</Link>.
              </p>
            ) : tickets.slice(0, 20).map((t, i) => <TicketRow key={t.ticket_id || i} t={t} />)}
        </div>

        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ ...card, flex: 2, minWidth: 260 }}>
            <div style={label}>Gift cards</div>
            {giftCards == null ? <p style={{ opacity: 0.6 }}>Loading…</p>
              : giftCards.length === 0 ? <p style={{ opacity: 0.7, marginBottom: 0 }}>None on this account. <Link to="/GiftCards" style={{ color: '#38BDF8' }}>Buy or redeem one</Link>.</p>
              : giftCards.map((g, i) => (
                <div key={g.code || i} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  <span style={{ fontFamily: 'monospace' }}>{g.code}</span>
                  <span>${((g.balance_cents ?? g.balanceCents ?? 0) / 100).toFixed(2)}</span>
                </div>
              ))}
          </div>
          <div style={{ ...card, flex: 1, minWidth: 220 }}>
            <div style={label}>More in the app</div>
            <p style={{ opacity: 0.75, fontSize: 14 }}>Loyalty rewards, Blast Pass, OrderUp food delivery and watch parties live in the StarView app.</p>
            <Link to="/get-the-app" style={{ color: '#141021', background: '#CFAA41', fontWeight: 800, padding: '9px 18px', borderRadius: 999, textDecoration: 'none', display: 'inline-block' }}>Get the app</Link>
          </div>
        </div>

        <button onClick={() => { logout(); navigate('/'); }}
          style={{ marginTop: 26, background: 'transparent', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 999, padding: '8px 20px', cursor: 'pointer' }}>
          Sign out
        </button>
      </div>
    </main>
  );
}
